import { useState } from 'react';
import { SpreadsheetCellProps } from '../types';
import { TextareaDialog } from './TextareaDialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Button } from '../../ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { DateTime } from 'luxon';

export function SpreadsheetCell({
  rowIdx,
  columnKey,
  value,
  isSelected,
  isActive,
  isSearchMatch,
  isCurrentSearchResult,
  isEditable,
  customStyle,
  dropdownOptions,
  useTextareaDialog,
  useCheckbox,
  useDatetime,
  useCommandPopover,
  commandPopoverOptions,
  onCellMouseDown,
  onCellMouseEnter,
  onCellChange,
  onCellBlur,
  onInputKeyDown,
  onCellFocus
}: SpreadsheetCellProps) {
  const EMPTY_CELL = '';
  const displayValue = value || EMPTY_CELL;
  const [isTextareaDialogOpen, setIsTextareaDialogOpen] = useState(false);
  const [isCommandPopoverOpen, setIsCommandPopoverOpen] = useState(false);

  const handleTextareaSave = (newValue: string) => {
    onCellChange(rowIdx, columnKey, newValue);
    onCellBlur(rowIdx, columnKey, newValue);
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!isEditable) {
    // For datetime columns, format timestamps as readable dates for display only
    let formattedDisplayValue = displayValue;
    if (useDatetime && displayValue != null && displayValue !== '') {
      try {
        const date = DateTime.fromMillis(Number(displayValue));
        if (!isNaN(date.toMillis())) {
          formattedDisplayValue = date.toLocaleString();
        }
      } catch {
        // Keep original value if conversion fails
      }
    }

    const showTruncated = useTextareaDialog && formattedDisplayValue && formattedDisplayValue.length > 50;
    
    return (
      <>
        <div 
          className={`h-full w-full text-sm cursor-default transition-colors text-gray-900 ${
            isSelected 
              ? 'spreadsheet-cell-selected' 
              : 'hover:bg-gray-100'
          } ${
            customStyle?.textAlign === 'center' ? 'flex items-center justify-center' :
            customStyle?.textAlign === 'right' ? 'flex items-center justify-end pr-3' :
            'flex items-center justify-start pl-3'
          } ${useTextareaDialog ? 'cursor-pointer' : ''}`}
          style={{ 
            margin: '0px', 
            padding: customStyle?.textAlign === 'right' ? '1px 6px 1px 1px' : 
                     customStyle?.textAlign === 'center' ? '1px 1px' : '1px 1px 1px 6px',
            fontFamily: customStyle?.fontFamily,
            fontWeight: customStyle?.fontWeight,
            fontSize: customStyle?.fontSize,
            backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                           isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
            color: customStyle?.color,
            border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
            boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
            position: isCurrentSearchResult ? 'relative' : undefined,
            zIndex: isCurrentSearchResult ? 1 : undefined
          }}
          data-row={rowIdx}
          data-col={columnKey}
          onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
          onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
          onClick={() => {
            if (useTextareaDialog) {
              setIsTextareaDialogOpen(true);
            }
          }}
          title={showTruncated ? formattedDisplayValue : undefined}
        >
          {showTruncated ? truncateText(formattedDisplayValue) : formattedDisplayValue}
          {showTruncated && (
            <span className="ml-1 text-blue-500 text-xs">📝</span>
          )}
        </div>
        
        {useTextareaDialog && (
          <TextareaDialog
            isOpen={isTextareaDialogOpen}
            onClose={() => setIsTextareaDialogOpen(false)}
            value={displayValue}
            onSave={handleTextareaSave}
            title={`Edit Notes (Row ${rowIdx + 1})`}
            placeholder="Enter notes..."
            maxLength={3000}
          />
        )}
      </>
    );
  }

  // If dropdown options are provided, render a select dropdown
  if (dropdownOptions && dropdownOptions.length > 0) {
    return (
      <div 
        className={`flex items-center h-full w-full relative transition-colors ${
          isSelected 
            ? 'spreadsheet-cell-selected' 
            : 'hover:bg-gray-100'
        }`}
        style={{ 
          margin: '0px', 
          padding: '0px',
          backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                         isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
          border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
          boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
          position: isCurrentSearchResult ? 'relative' : undefined,
          zIndex: isCurrentSearchResult ? 10 : undefined
        }}
        data-row={rowIdx}
        data-col={columnKey}
        onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
        onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
        onClick={(e) => {
          // Focus the select when the cell is clicked
          const select = e.currentTarget.querySelector('select') as HTMLSelectElement;
          if (select) {
            select.focus();
          }
        }}
      >
        <select
          value={displayValue}
          data-row={rowIdx}
          data-col={columnKey}
          onChange={(e) => onCellChange(rowIdx, columnKey, e.target.value)}
          onBlur={(e) => onCellBlur(rowIdx, columnKey, e.target.value)}
          onKeyDown={(e) => onInputKeyDown(e, rowIdx, columnKey)}
          onFocus={() => onCellFocus(rowIdx, columnKey)}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className={`w-full h-full px-3 text-sm border-none outline-none bg-transparent transition-colors text-gray-900 cursor-pointer ${
            isActive ? 'ring-2 ring-blue-400 ring-inset' : ''
          } ${isEditable ? 'focus:bg-blue-50' : ''}`}
          style={{
            fontFamily: customStyle?.fontFamily,
            fontWeight: customStyle?.fontWeight,
            fontSize: customStyle?.fontSize,
            textAlign: customStyle?.textAlign,
            color: customStyle?.color,
            pointerEvents: 'auto',
            zIndex: 1,
            boxSizing: 'border-box',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 8px center',
            backgroundSize: '16px',
            paddingRight: '28px'
          }}
        >
          <option value="">Select...</option>
          {dropdownOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // If this is a command popover column, render with command popover
  if (useCommandPopover && commandPopoverOptions && commandPopoverOptions.length > 0) {
    const selectedOption = commandPopoverOptions.find(option => option.value === displayValue);
    
    return (
      <div 
        className={`flex items-center h-full w-full relative transition-colors ${
          isSelected 
            ? 'spreadsheet-cell-selected' 
            : 'hover:bg-gray-100'
        }`}
        style={{ 
          margin: '0px', 
          padding: '0px',
          backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                         isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
          border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
          boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
          position: isCurrentSearchResult ? 'relative' : undefined,
          zIndex: isCurrentSearchResult ? 10 : undefined
        }}
        data-row={rowIdx}
        data-col={columnKey}
        onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
        onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
      >
        <Popover open={isCommandPopoverOpen} onOpenChange={setIsCommandPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={isCommandPopoverOpen}
              className="w-full h-full justify-between px-3 text-sm border-none outline-none bg-transparent hover:bg-transparent focus:bg-blue-50 text-gray-900 data-[state=open]:bg-transparent"
              style={{
                fontFamily: customStyle?.fontFamily,
                fontWeight: customStyle?.fontWeight,
                fontSize: customStyle?.fontSize,
                textAlign: customStyle?.textAlign,
                color: customStyle?.color,
                minHeight: 'auto',
                height: '100%',
                backgroundColor: 'transparent'
              }}
              disabled={!isEditable}
              onClick={(e) => {
                e.stopPropagation();
                if (isEditable) {
                  setIsCommandPopoverOpen(true);
                  onCellFocus(rowIdx, columnKey);
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span className="truncate text-left flex-1">
                {selectedOption?.label || displayValue || "Select..."}
              </span>
              {isEditable && <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search options..." />
              <CommandList>
                <CommandEmpty>No option found.</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      const newValue = '';
                      onCellChange(rowIdx, columnKey, newValue);
                      onCellBlur(rowIdx, columnKey, newValue);
                      setIsCommandPopoverOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        !displayValue ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-gray-500">None</span>
                  </CommandItem>
                  {commandPopoverOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      onSelect={() => {
                        onCellChange(rowIdx, columnKey, option.value);
                        onCellBlur(rowIdx, columnKey, option.value);
                        setIsCommandPopoverOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          displayValue === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  // If this is a checkbox column, render a checkbox
  if (useCheckbox) {
    const isChecked = value === 'Yes' || value === true || value === 'true';
    
    return (
      <div 
        className={`flex items-center justify-center h-full w-full relative transition-colors ${
          isSelected
            ? 'spreadsheet-cell-selected'
            : 'hover:bg-gray-100'
        }`}
        style={{ 
          margin: '0px', 
          padding: '0px',
          backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                         isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
          border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
          boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
          position: isCurrentSearchResult ? 'relative' : undefined,
          zIndex: isCurrentSearchResult ? 10 : undefined
        }}
        data-row={rowIdx}
        data-col={columnKey}
        onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
        onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
        onClick={(e) => {
          if (isEditable) {
            const newValue = !isChecked ? 'Yes' : 'No';
            onCellChange(rowIdx, columnKey, newValue);
            onCellBlur(rowIdx, columnKey, newValue);
          }
          e.stopPropagation();
        }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          data-row={rowIdx}
          data-col={columnKey}
          onChange={(e) => {
            if (isEditable) {
              const newValue = e.target.checked ? 'Yes' : 'No';
              onCellChange(rowIdx, columnKey, newValue);
              onCellBlur(rowIdx, columnKey, newValue);
            }
          }}
          onFocus={() => onCellFocus(rowIdx, columnKey)}
          onClick={(e) => e.stopPropagation()}
          className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 ${
            isEditable ? 'cursor-pointer' : 'cursor-default'
          }`}
          disabled={!isEditable}
          style={{
            pointerEvents: 'auto',
            zIndex: 1
          }}
        />
      </div>
    );
  }

  // If this is a datetime column, render a datetime input
  if (useDatetime) {
    // Convert timestamp to datetime-local format for input
    const getDatetimeValue = () => {
      if (!value || value === null || value === '') return '';
      try {
        // Value should be a timestamp from the database
        const date = DateTime.fromMillis(Number(value));
        if (isNaN(date.toMillis())) return '';
        
        // Format for datetime-local input (YYYY-MM-DDTHH:mm) in local time
        const year = date.year;
        const month = String(date.month).padStart(2, '0');
        const day = String(date.day).padStart(2, '0');
        const hours = String(date.hour).padStart(2, '0');
        const minutes = String(date.minute).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch {
        return '';
      }
    };

    const handleDatetimeChange = (newValue: string) => {
      let timestamp: number | null = null;
      if (newValue) {
        try {
          // Convert datetime-local input to timestamp for storage
          const date = DateTime.fromISO(newValue);
          if (date.isValid) {
            timestamp = date.toMillis();
          }
        } catch {
          // If conversion fails, keep as null
        }
      }
      onCellChange(rowIdx, columnKey, timestamp);
      // Also trigger blur to save the change immediately
      onCellBlur(rowIdx, columnKey, timestamp);
    };
    
    return (
      <div 
        className={`flex items-center h-full w-full relative transition-colors ${ 
          isSelected 
            ? 'spreadsheet-cell-selected' 
            : 'hover:bg-gray-100'
        }`}
        style={{ 
          margin: '0px', 
          padding: '0px',
          backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                         isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
          border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
          boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
          position: isCurrentSearchResult ? 'relative' : undefined,
          zIndex: isCurrentSearchResult ? 10 : undefined
        }}
        data-row={rowIdx}
        data-col={columnKey}
        onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
        onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
        onClick={(e) => {
          // Focus the input when the cell is clicked
          const input = e.currentTarget.querySelector('input') as HTMLInputElement;
          if (input) {
            input.focus();
          }
        }}
      >
        <input
          type="datetime-local"
          value={getDatetimeValue()}
          data-row={rowIdx}
          data-col={columnKey}
          onChange={(e) => handleDatetimeChange(e.target.value)}
          onBlur={(e) => {
            const formattedValue = e.target.value ? DateTime.fromISO(e.target.value).toLocaleString() : '';
            onCellBlur(rowIdx, columnKey, formattedValue);
          }}
          onKeyDown={(e) => onInputKeyDown(e, rowIdx, columnKey)}
          onFocus={() => onCellFocus(rowIdx, columnKey)}
          onClick={(e) => e.stopPropagation()}
          className={`w-full h-full px-3 text-sm border-none outline-none bg-transparent transition-colors text-gray-900 dark:text-gray-900 ${ 
            isActive ? 'ring-2 ring-blue-400 ring-inset' : ''
          } ${isEditable ? 'focus:bg-blue-50' : ''}`}
          style={{
            fontFamily: customStyle?.fontFamily,
            fontWeight: customStyle?.fontWeight,
            fontSize: customStyle?.fontSize,
            textAlign: customStyle?.textAlign,
            color: customStyle?.color || '#111827',
            pointerEvents: 'auto',
            zIndex: 1,
            boxSizing: 'border-box'
          }}
          disabled={!isEditable}
        />
      </div>
    );
  }

  // If this is a textarea column, render a click-to-edit button
  if (useTextareaDialog) {
    const showTruncated = displayValue && displayValue.length > 50;
    
    return (
      <>
        <div 
          className={`flex items-center h-full w-full relative transition-colors cursor-pointer ${
            isSelected 
              ? 'spreadsheet-cell-selected' 
              : 'hover:bg-gray-100'
          }`}
          style={{ 
            margin: '0px', 
            padding: '0px',
            backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                           isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
            border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
            boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
            position: isCurrentSearchResult ? 'relative' : undefined,
            zIndex: isCurrentSearchResult ? 1 : undefined
          }}
          data-row={rowIdx}
          data-col={columnKey}
          onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
          onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
          onClick={() => {
            setIsTextareaDialogOpen(true);
            onCellFocus(rowIdx, columnKey);
          }}
          title={showTruncated ? displayValue : "Click to edit"}
        >
          <div className="flex items-center justify-between w-full px-3 text-sm">
            <span className="truncate text-gray-900" style={{
              fontFamily: customStyle?.fontFamily,
              fontWeight: customStyle?.fontWeight,
              fontSize: customStyle?.fontSize,
              color: customStyle?.color
            }}>
              {showTruncated ? truncateText(displayValue) : (displayValue || 'Click to edit...')}
            </span>
            <span className="ml-2 text-blue-500 text-xs flex-shrink-0">
              {showTruncated ? '📝' : '✏️'}
            </span>
          </div>
        </div>
        
        <TextareaDialog
          isOpen={isTextareaDialogOpen}
          onClose={() => setIsTextareaDialogOpen(false)}
          value={displayValue}
          onSave={handleTextareaSave}
          title={`Edit Notes (Row ${rowIdx + 1})`}
          placeholder="Enter notes..."
          maxLength={3000}
        />
      </>
    );
  }

  return (
    <div 
      className={`flex items-center h-full w-full relative transition-colors ${
        isSelected 
          ? 'spreadsheet-cell-selected' 
          : 'hover:bg-gray-100'
      }`}
      style={{ 
        margin: '0px', 
        padding: '0px',
        backgroundColor: isCurrentSearchResult ? '#fef3c7' : 
                       isSearchMatch ? '#fef3c7' : customStyle?.backgroundColor,
        border: isCurrentSearchResult ? '2px solid #f59e0b' : undefined,
        boxShadow: isCurrentSearchResult ? '0 0 0 1px #f59e0b' : undefined,
        position: isCurrentSearchResult ? 'relative' : undefined,
        zIndex: isCurrentSearchResult ? 1 : undefined
      }}
      data-row={rowIdx}
      data-col={columnKey}
      onMouseDown={(e) => onCellMouseDown(rowIdx, columnKey, e)}
      onMouseEnter={() => onCellMouseEnter(rowIdx, columnKey)}
      onClick={(e) => {
        // Focus the input when the cell is clicked
        const input = e.currentTarget.querySelector('input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }}
    >
      <input
        type="text"
        value={displayValue}
        data-row={rowIdx}
        data-col={columnKey}
        onChange={(e) => onCellChange(rowIdx, columnKey, e.target.value)}
        onBlur={(e) => onCellBlur(rowIdx, columnKey, e.target.value)}
        onKeyDown={(e) => onInputKeyDown(e, rowIdx, columnKey)}
        onFocus={() => onCellFocus(rowIdx, columnKey)}
        onClick={(e) => {
          e.stopPropagation();
          const input = e.target as HTMLInputElement;
          input.focus();
          input.select();
        }}
        className={`w-full h-full px-1 text-sm border-none outline-none bg-transparent transition-colors text-gray-900 ${
          isActive ? 'ring-2 ring-blue-400 ring-inset' : ''
        } ${isEditable ? 'focus:bg-blue-50' : ''}`}
        style={{
          fontFamily: customStyle?.fontFamily,
          fontWeight: customStyle?.fontWeight,
          fontSize: customStyle?.fontSize,
          textAlign: customStyle?.textAlign,
          color: customStyle?.color,
          pointerEvents: 'auto',
          zIndex: 0,
          boxSizing: 'border-box'
        }}
      />
    </div>
  );
} 